export const DESIGN_REMOVED_MESSAGE = "onDesignRemoved";

export interface DesignRemovedMessage {
  readonly type: typeof DESIGN_REMOVED_MESSAGE;
  readonly data: { readonly designId: string | null };
}

interface ArchiveResponse {
  json: () => Promise<{ errors?: ReadonlyArray<{ message?: string }> }>;
}

interface TrackerDeps {
  readonly notifyParent: (message: DesignRemovedMessage) => void;
  readonly archiveDesign: (designId: string) => Promise<ArchiveResponse>;
}

export interface UploadedDesignTracker {
  readonly onUploaded: (design?: { id?: string | number } | null) => void;
  readonly onRemoved: () => Promise<void>;
}

const archiveOrThrow = async (archiveDesign: TrackerDeps["archiveDesign"], designId: string): Promise<void> => {
  const body = await (await archiveDesign(designId)).json();
  if (body.errors && body.errors.length > 0) {
    throw new Error(body.errors.map((error) => error.message).join("; "));
  }
};

// The design row is created as soon as the PDF uploads, so removing the file must archive it and tell the host page.
export const createUploadedDesignTracker = ({ notifyParent, archiveDesign }: TrackerDeps): UploadedDesignTracker => {
  let designId: string | null = null;

  return {
    onUploaded: (design) => {
      designId = design?.id != null ? String(design.id) : null;
    },
    onRemoved: async () => {
      const removedId = designId;
      designId = null;
      notifyParent({ type: DESIGN_REMOVED_MESSAGE, data: { designId: removedId } });
      if (!removedId) return;
      try {
        await archiveOrThrow(archiveDesign, removedId);
      } catch (error) {
        console.error(`DesignUpload: failed to archive removed design ${removedId}`, error);
      }
    },
  };
};
